// client/src/coms/components/MembersPanel.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { StatusDot, StatusTag, statusTitle } from '../presence/PresenceUi';
import { statusFor, usePresence } from '../presence/presenceClient';
import { useMyPermissions } from '../usePermissions';
import { comsApiGet, comsApiPatch, comsApiDelete } from '../comsApi';
import { getParticipantPrefs, setParticipantPrefs } from '../voip/participantPrefs';

const SUPPORT_BANNER_HEIGHT = 65;

function safeArr(v) {
  return Array.isArray(v) ? v : [];
}

function safeObj(v) {
  return v && typeof v === 'object' ? v : {};
}

function pickServerKey(server) {
  const s = server && typeof server === 'object' ? server : null;
  const v = s && (s.id || s.server_id || s.public_id) ? String(s.id || s.server_id || s.public_id) : '';
  return String(v || '').trim();
}

function toInt(v, fallback = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? Math.floor(n) : fallback;
}

function toInt01(v) {
  if (v === true) return 1;
  if (v === false) return 0;
  const n = Number(v);
  return n === 1 ? 1 : 0;
}

function clampInt(n, min, max) {
  const v = toInt(n, min);
  if (v < min) return min;
  if (v > max) return max;
  return v;
}

function normalizeMember(m) {
  const o = m && typeof m === 'object' ? m : {};
  const id = o.id != null ? String(o.id) : o.user_id != null ? String(o.user_id) : '';
  const name = o.name != null ? String(o.name) : o.username != null ? String(o.username) : id ? `User ${id}` : 'User';
  const statusRaw = o.status != null ? String(o.status) : '';
  const status = statusRaw.trim() ? statusRaw.trim() : 'offline';

  return {
    ...o,
    id,
    name,
    status,
    is_banned: toInt01(o.is_banned),
    banned: toInt01(o.banned),
  };
}

function isShownOnline(status) {
  const s = String(status || '').toLowerCase();
  return s === 'online' || s === 'idle' || s === 'dnd' || s === 'invisible';
}

function normalizeNameForCompare(v) {
  const s = String(v == null ? '' : v).trim();
  return s.toLowerCase();
}

function membersShallowEqual(a, b) {
  const aa = safeArr(a);
  const bb = safeArr(b);
  if (aa.length !== bb.length) return false;

  for (let i = 0; i < aa.length; i += 1) {
    const am = normalizeMember(aa[i]);
    const bm = normalizeMember(bb[i]);
    if (String(am.id || '') !== String(bm.id || '')) return false;
    if (String(am.name || '') !== String(bm.name || '')) return false;
    if (String(am.status || '') !== String(bm.status || '')) return false;
    if (toInt01(am.is_banned) !== toInt01(bm.is_banned)) return false;
  }

  return true;
}

function clampMenuPos(x, y, menuW = 260, menuH = 320) {
  try {
    const pad = 8;
    const vw = typeof window !== 'undefined' ? window.innerWidth : 99999;
    const vh = typeof window !== 'undefined' ? window.innerHeight : 99999;

    let nx = Number.isFinite(x) ? x : 0;
    let ny = Number.isFinite(y) ? y : 0;

    if (nx + menuW > vw - pad) nx = Math.max(pad, vw - menuW - pad);
    if (ny + menuH > vh - pad) ny = Math.max(pad, vh - menuH - pad);

    if (nx < pad) nx = pad;
    if (ny < pad) ny = pad;

    return { x: nx, y: ny };
  } catch {
    return { x, y };
  }
}

function clampSubmenuPos(parentX, parentY, parentW, submenuW = 240, submenuH = 280) {
  try {
    const pad = 8;
    const vw = typeof window !== 'undefined' ? window.innerWidth : 99999;
    const vh = typeof window !== 'undefined' ? window.innerHeight : 99999;

    let nx = parentX + parentW - 6;
    let ny = parentY + 96;

    if (nx + submenuW > vw - pad) nx = Math.max(pad, parentX - submenuW + 6);
    if (ny + submenuH > vh - pad) ny = Math.max(pad, vh - submenuH - pad);

    if (nx < pad) nx = pad;
    if (ny < pad) ny = pad;

    return { x: nx, y: ny };
  } catch {
    return { x: parentX, y: parentY };
  }
}

function normalizeRoleRow(role) {
  const r = role && typeof role === 'object' ? role : {};
  return {
    ...r,
    id: toInt(r.id, 0),
    name: String(r.name || '').trim(),
    color: String(r.color || '').trim(),
    position: toInt(r.position, 0),
    is_default: toInt01(r.is_default),
    is_owner: toInt01(r.is_owner),
  };
}

function isOwnerRole(role) {
  const r = normalizeRoleRow(role);
  return r.is_owner === 1;
}

function pickTopRoleColor(roles) {
  const sorted = safeArr(roles)
    .map(normalizeRoleRow)
    .filter((r) => r.id > 0)
    .sort((a, b) => {
      if (b.position !== a.position) return b.position - a.position;
      return a.id - b.id;
    });

  const withColor = sorted.find((r) => String(r.color || '').trim());
  return withColor ? String(withColor.color || '').trim() : '';
}

function normalizePinnedMessage(msg) {
  const m = msg && typeof msg === 'object' ? msg : {};
  return {
    ...m,
    id: toInt(m.id, 0),
    user_id: toInt(m.user_id, 0),
    author: String(m.author || m.username || m.display_name || '').trim(),
    stamp: String(m.stamp || '').trim(),
    text: String(m.body || m.text || '').replace(/\r\n/g, '\n').replace(/\r/g, '\n').trim(),
    is_pinned: toInt01(m.is_pinned),
    is_motd: toInt01(m.is_motd),
    is_super_pinned: toInt01(m.is_super_pinned),
    is_header_message: toInt01(m.is_header_message),
  };
}

function buildPinnedPreview(msg, maxLen = 120) {
  const m = normalizePinnedMessage(msg);
  const raw = String(m.text || '').replace(/\s+/g, ' ').trim();
  if (!raw) return '(empty)';
  if (raw.length <= maxLen) return raw;
  return `${raw.slice(0, maxLen - 1).trim()}…`;
}

async function putJson(url, body) {
  const res = await fetch(`/api/coms${url}`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body || {}),
  });

  const data = await res.json().catch(() => null);

  if (!res.ok) {
    const msg = (data && (data.error || data.detail)) || `Request failed with status ${res.status}`;
    throw new Error(String(msg || 'Request failed'));
  }

  return data;
}

async function postComsJson(url, body) {
  const res = await fetch(`/api/coms${url}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body || {}),
  });

  const data = await res.json().catch(() => null);

  if (!res.ok) {
    const msg = (data && (data.error || data.detail)) || `Request failed with status ${res.status}`;
    throw new Error(String(msg || 'Request failed'));
  }

  return data;
}

export default function MembersPanel({
  server,
  user,
  messageSearch = '',
  onMessageSearchChange,
  panelTab = 'members',
  onPanelTabChange,
  pinnedMessages = [],
  onJumpToPinnedMessage,
  onRemovePinnedMessage,
}) {
  const serverKey = useMemo(() => pickServerKey(server), [server]);
  // Server-resolved permissions (roles + legacy defaults), refreshed when they change.
  const memberPerms = useMyPermissions(serverKey);
  const canVoiceMute = memberPerms.loaded && memberPerms.has('mute_members');
  const canVoiceDeafen = memberPerms.loaded && memberPerms.has('deafen_members');
  const canVoiceMove = memberPerms.loaded && memberPerms.has('move_members');
  const voiceChannelsForMove = useMemo(
    () => (Array.isArray(server && server.channels) ? server.channels : []).filter((c) => c && c.kind === 'voice').map((c) => ({ id: Number(c.db_id != null ? c.db_id : c.id) || 0, name: c.name })).filter((c) => c.id > 0),
    [server]
  );

  const serverIdForPrefs = useMemo(() => {
    const s = server && typeof server === 'object' ? server : null;
    const v = s && (s.db_id || s.server_db_id || s._db_id || s.id || s.server_id || s.public_id)
      ? String(s.db_id || s.server_db_id || s._db_id || s.id || s.server_id || s.public_id)
      : '';
    return String(v || '').trim();
  }, [server]);

  const ownerUserId = useMemo(() => {
    const s = safeObj(server);
    const id =
      s.owner_user_id != null
        ? s.owner_user_id
        : s.ownerId != null
          ? s.ownerId
          : s.owner_userId != null
            ? s.owner_userId
            : null;
    return toInt(id, 0);
  }, [server]);

  const passedUserId = useMemo(() => {
    const u = safeObj(user);
    const id = u.id != null ? u.id : u.user_id != null ? u.user_id : null;
    return toInt(id, 0);
  }, [user]);

  const [remoteMembers, setRemoteMembers] = useState([]);
  const [remoteMembersLoaded, setRemoteMembersLoaded] = useState(false);
  const [loading, setLoading] = useState(false);

  const [bannedMembers, setBannedMembers] = useState([]);
  const [bannedMembersLoaded, setBannedMembersLoaded] = useState(false);
  const [bannedMembersLoading, setBannedMembersLoading] = useState(false);

  const passedMembers = useMemo(() => {
    const s = server && typeof server === 'object' ? server : null;
    return safeArr(s && s.members);
  }, [server]);

  const baseMembers = useMemo(() => {
    const base = remoteMembersLoaded ? remoteMembers : passedMembers;
    return safeArr(base).map(normalizeMember).filter((m) => m && m.id);
  }, [passedMembers, remoteMembers, remoteMembersLoaded]);

  const normalizedBannedMembers = useMemo(() => {
    return safeArr(bannedMembers).map(normalizeMember).filter((m) => m && m.id);
  }, [bannedMembers]);

  const presence = usePresence();
  const [myUserId, setMyUserId] = useState(() => passedUserId);
  const [memberTopRoleColorByUserId, setMemberTopRoleColorByUserId] = useState({});
  const [blockedUsers, setBlockedUsers] = useState([]);

  const members = useMemo(() => {
    return baseMembers.map((m) => {
      const uid = toInt(m.id, 0);
      // Invisible members are simply absent from the feed, so they look offline; only your own row shows Invisible.
      const pres = statusFor(presence, uid, toInt(myUserId, 0));
      const roleColor = uid > 0 ? String(memberTopRoleColorByUserId[String(uid)] || '') : '';
      return { ...m, status: pres.status, custom_text: pres.status === 'invisible' || pres.status === 'offline' ? '' : pres.custom_text, roleColor };
    });
  }, [baseMembers, presence, myUserId, memberTopRoleColorByUserId]);

  const enrichedBannedMembers = useMemo(() => {
    return normalizedBannedMembers.map((m) => {
      const uid = toInt(m.id, 0);
      const roleColor = uid > 0 ? String(memberTopRoleColorByUserId[String(uid)] || '') : '';
      return { ...m, status: 'banned', is_banned: 1, banned: 1, roleColor };
    });
  }, [normalizedBannedMembers, memberTopRoleColorByUserId]);

  const onlineMembers = useMemo(() => members.filter((m) => isShownOnline(m.status)), [members]);
  const offlineMembers = useMemo(() => members.filter((m) => !isShownOnline(m.status)), [members]);

  const [menu, setMenu] = useState({ open: false, x: 0, y: 0, member: null });
  const [prefs, setPrefs] = useState({ volume: 100, muted: false });
  const menuRef = useRef(null);
  const rolesMenuRef = useRef(null);

  const [myNameOverride, setMyNameOverride] = useState('');
  const [myEffectiveName, setMyEffectiveName] = useState('');
  const [myBaseName, setMyBaseName] = useState('');
  const [myRoles, setMyRoles] = useState([]);

  const [availableRoles, setAvailableRoles] = useState([]);
  const [memberRoleIds, setMemberRoleIds] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [rolesSaving, setRolesSaving] = useState(false);
  const [rolesError, setRolesError] = useState('');
  const [rolesMenuOpen, setRolesMenuOpen] = useState(false);
  const [friendActionBusy, setFriendActionBusy] = useState(false);
  const [friendActionMsg, setFriendActionMsg] = useState('');
  const [blockActionBusy, setBlockActionBusy] = useState(false);
  const [blockActionMsg, setBlockActionMsg] = useState('');
  const [pinActionBusyById, setPinActionBusyById] = useState({});
  const [pinActionError, setPinActionError] = useState('');
  const [moderationCapabilities, setModerationCapabilities] = useState(null);
  const [moderationLoading, setModerationLoading] = useState(false);
  const [moderationActionBusy, setModerationActionBusy] = useState(false);
  const [moderationActionMsg, setModerationActionMsg] = useState('');

  const searchValue = String(messageSearch || '');
  const searchNorm = useMemo(() => searchValue.trim().toLowerCase(), [searchValue]);

  const menuMemberId = useMemo(() => toInt(menu && menu.member && menu.member.id, 0), [menu]);
  const menuMemberIsBanned = useMemo(() => toInt01(menu && menu.member && (menu.member.is_banned || menu.member.banned)) === 1, [menu]);

  const blockedUserIdSet = useMemo(() => {
    const set = new Set();
    safeArr(blockedUsers).forEach((item) => {
      const uid = toInt(item && item.blocked_user_id, 0);
      if (uid > 0) set.add(uid);
    });
    return set;
  }, [blockedUsers]);

  const menuMemberIsBlocked = useMemo(() => {
    return menuMemberId > 0 ? blockedUserIdSet.has(menuMemberId) : false;
  }, [blockedUserIdSet, menuMemberId]);

  const isOwner = useMemo(() => {
    return !!myUserId && !!ownerUserId && myUserId === ownerUserId;
  }, [myUserId, ownerUserId]);

  const myPermissionsMap = useMemo(() => {
    const out = {};

    safeArr(myRoles).forEach((role) => {
      const roleObj = safeObj(role);
      const permissionsMap = safeObj(roleObj.permissions_map);

      Object.keys(permissionsMap).forEach((key) => {
        if (toInt(permissionsMap[key], 0) === 1) out[key] = 1;
      });

      safeArr(roleObj.permissions).forEach((perm) => {
        const key = String(perm && perm.key ? perm.key : '').trim();
        if (!key) return;
        if (toInt(perm && perm.allow, 0) === 1) out[key] = 1;
      });
    });

    return out;
  }, [myRoles]);

  const canManagePins = useMemo(() => {
    if (isOwner) return true;
    return toInt(myPermissionsMap.manage_messages, 0) === 1;
  }, [isOwner, myPermissionsMap]);

  const canModerateMembers = useMemo(() => {
    if (isOwner) return true;
    if (canVoiceMute || canVoiceDeafen || canVoiceMove) return true;
    return toInt(myPermissionsMap.ban_members, 0) === 1 || toInt(myPermissionsMap.kick_members, 0) === 1;
  }, [isOwner, myPermissionsMap, canVoiceMute, canVoiceDeafen, canVoiceMove]);

  const showBannedTab = canModerateMembers;

  const activeTab = useMemo(() => {
    const raw = String(panelTab || '').trim().toLowerCase();
    if (raw === 'pins') return 'pins';
    if (raw === 'banned' && showBannedTab) return 'banned';
    return 'members';
  }, [panelTab, showBannedTab]);

  const canManageRolesHere = useMemo(() => {
    if (!menu.open) return false;
    if (!serverKey) return false;
    // Owner or Manage roles; the server only allows roles and members below your highest role.
    if (!isOwner && !(memberPerms.loaded && memberPerms.has('manage_roles'))) return false;
    if (!menuMemberId) return false;
    if (menuMemberId === myUserId) return false;
    if (menuMemberIsBanned) return false;
    return true;
  }, [menu.open, serverKey, isOwner, menuMemberId, myUserId, menuMemberIsBanned, memberPerms]);

  const canRequestFriendHere = useMemo(() => {
    if (!menu.open) return false;
    if (!menuMemberId) return false;
    if (!myUserId) return false;
    if (menuMemberId === myUserId) return false;
    if (menuMemberIsBlocked) return false;
    if (menuMemberIsBanned) return false;
    return true;
  }, [menu.open, menuMemberId, myUserId, menuMemberIsBlocked, menuMemberIsBanned]);

  const canToggleBlockHere = useMemo(() => {
    if (!menu.open) return false;
    if (!menuMemberId) return false;
    if (!myUserId) return false;
    if (menuMemberIsBanned) return false;
    return menuMemberId !== myUserId;
  }, [menu.open, menuMemberId, myUserId, menuMemberIsBanned]);

  const canBanHere = useMemo(() => {
    return !!(moderationCapabilities && toInt01(moderationCapabilities.can_ban) === 1 && !menuMemberIsBanned);
  }, [moderationCapabilities, menuMemberIsBanned]);

  const canTimeoutHere = !!(moderationCapabilities && toInt01(moderationCapabilities.can_timeout) === 1 && !menuMemberIsBanned);
  const menuTimeoutUntil = Number((moderationCapabilities && moderationCapabilities.timeout_until) || 0);

  const canKickHere = useMemo(() => {
    return !!(moderationCapabilities && toInt01(moderationCapabilities.can_kick) === 1 && !menuMemberIsBanned);
  }, [moderationCapabilities, menuMemberIsBanned]);

  const canUnbanHere = useMemo(() => {
    if (!menuMemberIsBanned) return false;
    if (isOwner) return true;
    return toInt(myPermissionsMap.ban_members, 0) === 1;
  }, [menuMemberIsBanned, isOwner, myPermissionsMap]);

  const sortedAvailableRoles = useMemo(() => {
    return safeArr(availableRoles)
      .map(normalizeRoleRow)
      .filter((r) => r.id > 0 && r.name)
      .sort((a, b) => {
        if (b.position !== a.position) return b.position - a.position;
        return a.id - b.id;
      });
  }, [availableRoles]);

  const selectedRoleId = useMemo(() => {
    if (!memberRoleIds.length) return 0;
    return toInt(memberRoleIds[0], 0);
  }, [memberRoleIds]);

  const rolesMenuPos = useMemo(() => {
    return clampSubmenuPos(menu.x, menu.y, 260, 240, 320);
  }, [menu.x, menu.y]);

  const normalizedPinnedMessages = useMemo(() => {
    return safeArr(pinnedMessages)
      .map(normalizePinnedMessage)
      .filter((m) => m.id > 0 && m.is_pinned === 1)
      .sort((a, b) => b.id - a.id);
  }, [pinnedMessages]);

  function handleBmcPopup() {
    const url = 'https://www.buymeacoffee.com/Point_Atlas';
    const width = 450;
    const height = 700;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;
    window.open(
      url,
      '_blank',
      `toolbar=no,location=no,status=no,menubar=no,scrollbars=yes,resizable=yes,width=${width},height=${height},left=${left},top=${top}`
    );
  }

  async function refreshBannedMembers() {
    if (!serverKey || !showBannedTab) {
      setBannedMembers([]);
      setBannedMembersLoaded(false);
      return;
    }

    setBannedMembersLoading(true);

    try {
      const res = await comsApiGet(`/servers/${encodeURIComponent(serverKey)}/banned-members`).catch(() => null);
      const list = res && res.ok && Array.isArray(res.members) ? res.members : [];
      setBannedMembers((prev) => (membersShallowEqual(prev, list) ? prev : list));
      setBannedMembersLoaded(true);
    } catch {
      setBannedMembers([]);
      setBannedMembersLoaded(true);
    } finally {
      setBannedMembersLoading(false);
    }
  }

  useEffect(() => {
    let alive = true;

    async function loadBlockedUsers() {
      try {
        const res = await comsApiGet('/blocks').catch(() => null);
        if (!alive) return;
        setBlockedUsers(safeArr(res && res.blocks));
      } catch {
        if (!alive) return;
        setBlockedUsers([]);
      }
    }

    loadBlockedUsers().catch(() => {});
    return () => {
      alive = false;
    };
  }, [serverKey]);

  useEffect(() => {
    if (passedUserId > 0) setMyUserId(passedUserId);
  }, [passedUserId]);

  useEffect(() => {
    let cancelled = false;

    async function loadMyRoles() {
      const sid = String(serverKey || '').trim();
      const uid = toInt(myUserId, 0);

      if (!sid || !uid) {
        if (!cancelled) setMyRoles([]);
        return;
      }

      try {
        const res = await comsApiGet(
          `/servers/${encodeURIComponent(sid)}/members/${encodeURIComponent(String(uid))}/roles`
        ).catch(() => null);

        if (cancelled) return;
        setMyRoles(safeArr(res && res.roles));
      } catch {
        if (cancelled) return;
        setMyRoles([]);
      }
    }

    loadMyRoles().catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [serverKey, myUserId]);

  useEffect(() => {
    let alive = true;

    async function loadMyServerName() {
      if (!serverKey) {
        if (alive) {
          setMyNameOverride('');
          setMyEffectiveName('');
          setMyBaseName('');
          if (!passedUserId) setMyUserId(0);
        }
        return;
      }

      try {
        const me = await comsApiGet(`/servers/${encodeURIComponent(serverKey)}/me`).catch(() => null);
        if (!alive) return;

        if (me && me.ok) {
          const base = String(me.username || '').trim();
          const dn = String(me.display_name || '').trim();
          const effective = String(me.effective_name || '').trim();
          const uid = toInt(me.user_id, 0);

          const same = dn && base && normalizeNameForCompare(dn) === normalizeNameForCompare(base);

          setMyBaseName(base);
          setMyEffectiveName(effective || dn || base);

          if (uid > 0) setMyUserId(uid);
          else if (passedUserId > 0) setMyUserId(passedUserId);

          if (dn && !same) setMyNameOverride(dn);
          else setMyNameOverride('');
        } else {
          setMyNameOverride('');
          setMyEffectiveName('');
          setMyBaseName('');
          if (passedUserId > 0) setMyUserId(passedUserId);
        }
      } catch {
        if (alive) {
          setMyNameOverride('');
          setMyEffectiveName('');
          setMyBaseName('');
          if (passedUserId > 0) setMyUserId(passedUserId);
        }
      }
    }

    loadMyServerName();
    return () => {
      alive = false;
    };
  }, [serverKey, passedUserId]);

  useEffect(() => {
    let alive = true;
    let membersTimer = null;
    let metaTimer = null;

    async function refreshMembers() {
      if (!serverKey) {
        if (alive) {
          setRemoteMembers([]);
          setRemoteMembersLoaded(false);
        }
        return;
      }

      try {
        const direct = await comsApiGet(`/servers/${encodeURIComponent(serverKey)}/members`).catch(() => null);

        if (!alive) return;

        if (direct && direct.ok && Array.isArray(direct.members)) {
          setRemoteMembers((prev) => (membersShallowEqual(prev, direct.members) ? prev : direct.members));
          setRemoteMembersLoaded(true);
          return;
        }

        const all = await comsApiGet('/servers').catch(() => null);
        if (!alive) return;

        if (all && all.ok && Array.isArray(all.servers)) {
          const found = all.servers.find((s) => pickServerKey(s) === serverKey);
          const list = safeArr(found && found.members);
          setRemoteMembers((prev) => (membersShallowEqual(prev, list) ? prev : list));
          setRemoteMembersLoaded(true);
          return;
        }

        setRemoteMembers([]);
        setRemoteMembersLoaded(true);
      } catch {
        if (alive) {
          setRemoteMembers([]);
          setRemoteMembersLoaded(true);
        }
      }
    }

    async function refreshMeta() {
      if (!serverKey) return;
      try {
        await comsApiGet('/servers').catch(() => null);
      } catch {}
    }

    async function initialLoad() {
      if (alive) setLoading(true);
      try {
        await Promise.all([refreshMembers(), refreshMeta(), showBannedTab ? refreshBannedMembers() : Promise.resolve()]);
      } finally {
        if (alive) setLoading(false);
      }
    }

    initialLoad();

    membersTimer = setInterval(() => {
      refreshMembers();
      if (showBannedTab) refreshBannedMembers();
    }, 4000);
    metaTimer = setInterval(refreshMeta, 10000);

    return () => {
      alive = false;
      if (membersTimer) clearInterval(membersTimer);
      if (metaTimer) clearInterval(metaTimer);
    };
  }, [serverKey, showBannedTab]);

  useEffect(() => {
    let alive = true;

    async function loadMemberRoleColors() {
      if (!serverKey) {
        if (alive) setMemberTopRoleColorByUserId({});
        return;
      }

      const memberIds = [
        ...safeArr(baseMembers).map((m) => toInt(m && m.id, 0)),
        ...safeArr(normalizedBannedMembers).map((m) => toInt(m && m.id, 0)),
      ].filter((id) => id > 0);

      const uniqueIds = Array.from(new Set(memberIds));
      if (!uniqueIds.length) {
        if (alive) setMemberTopRoleColorByUserId({});
        return;
      }

      const results = await Promise.all(
        uniqueIds.map(async (uid) => {
          try {
            const res = await comsApiGet(
              `/servers/${encodeURIComponent(serverKey)}/members/${encodeURIComponent(String(uid))}/roles`
            ).catch(() => null);

            const roles = res && res.ok && Array.isArray(res.roles) ? res.roles : [];
            return [String(uid), pickTopRoleColor(roles)];
          } catch {
            return [String(uid), ''];
          }
        })
      );

      if (!alive) return;

      const nextMap = {};
      for (const entry of results) {
        const key = entry && entry[0] ? String(entry[0]) : '';
        const color = entry && entry[1] ? String(entry[1]) : '';
        if (key) nextMap[key] = color;
      }

      setMemberTopRoleColorByUserId(nextMap);
    }

    loadMemberRoleColors();

    return () => {
      alive = false;
    };
  }, [serverKey, baseMembers, normalizedBannedMembers]);

  useEffect(() => {
    async function loadModerationCapabilities() {
      if (!menu.open || !serverKey || !menuMemberId || !canModerateMembers || menuMemberId === myUserId) {
        setModerationCapabilities(null);
        setModerationLoading(false);
        return;
      }

      setModerationLoading(true);
      try {
        const res = await comsApiGet(
          `/servers/${encodeURIComponent(serverKey)}/members/${encodeURIComponent(String(menuMemberId))}/moderation-capabilities`
        ).catch(() => null);

        if (!res || !res.ok) {
          setModerationCapabilities(null);
          return;
        }

        setModerationCapabilities(res);
      } catch {
        setModerationCapabilities(null);
      } finally {
        setModerationLoading(false);
      }
    }

    loadModerationCapabilities().catch(() => {});
  }, [menu.open, serverKey, menuMemberId, canModerateMembers, myUserId]);

  useEffect(() => {
    function insideAny(target) {
      try {
        if (menuRef.current && menuRef.current.contains(target)) return true;
        if (rolesMenuRef.current && rolesMenuRef.current.contains(target)) return true;
      } catch {}
      return false;
    }

    function resetMenus() {
      setMenu({ open: false, x: 0, y: 0, member: null });
      setAvailableRoles([]);
      setMemberRoleIds([]);
      setRolesLoading(false);
      setRolesSaving(false);
      setRolesError('');
      setRolesMenuOpen(false);
      setFriendActionBusy(false);
      setFriendActionMsg('');
      setBlockActionBusy(false);
      setBlockActionMsg('');
      setModerationCapabilities(null);
      setModerationLoading(false);
      setModerationActionBusy(false);
      setModerationActionMsg('');
    }

    function onDocMouseDown(e) {
      if (!menu.open) return;
      if (insideAny(e.target)) return;
      resetMenus();
    }

    function onDocContextMenu(e) {
      if (!menu.open) return;
      if (insideAny(e.target)) return;
      resetMenus();
    }

    function onKeyDown(e) {
      if (!menu.open) return;
      if (e.key === 'Escape') resetMenus();
    }

    function onScroll() {
      if (!menu.open) return;
      resetMenus();
    }

    document.addEventListener('mousedown', onDocMouseDown, true);
    document.addEventListener('contextmenu', onDocContextMenu, true);
    document.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('scroll', onScroll, true);

    return () => {
      document.removeEventListener('mousedown', onDocMouseDown, true);
      document.removeEventListener('contextmenu', onDocContextMenu, true);
      document.removeEventListener('keydown', onKeyDown, true);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [menu.open]);

  async function loadRoleMenuData(nextMember) {
    const targetId = nextMember && nextMember.id != null ? toInt(nextMember.id, 0) : 0;

    if (!serverKey || !targetId || !isOwner || targetId === myUserId || toInt01(nextMember && (nextMember.is_banned || nextMember.banned)) === 1) {
      setAvailableRoles([]);
      setMemberRoleIds([]);
      setRolesLoading(false);
      setRolesSaving(false);
      setRolesError('');
      return;
    }

    setRolesLoading(true);
    setRolesSaving(false);
    setRolesError('');

    try {
      const [rolesRes, memberRolesRes] = await Promise.all([
        comsApiGet(`/servers/${encodeURIComponent(serverKey)}/roles`).catch(() => null),
        comsApiGet(`/servers/${encodeURIComponent(serverKey)}/members/${encodeURIComponent(String(targetId))}/roles`).catch(() => null),
      ]);

      const roles = (rolesRes && rolesRes.ok && Array.isArray(rolesRes.roles) ? rolesRes.roles : [])
        .map(normalizeRoleRow)
        .filter((r) => r.id > 0 && r.name && !isOwnerRole(r));

      const assigned = (memberRolesRes && memberRolesRes.ok && Array.isArray(memberRolesRes.roles) ? memberRolesRes.roles : [])
        .map(normalizeRoleRow)
        .filter((r) => r.id > 0 && !isOwnerRole(r));

      const sortedAssigned = safeArr(assigned).sort((a, b) => {
        if (b.position !== a.position) return b.position - a.position;
        return a.id - b.id;
      });

      setAvailableRoles(roles);
      setMemberRoleIds(sortedAssigned.length ? [sortedAssigned[0].id] : []);
      setRolesError('');
    } catch (e) {
      setAvailableRoles([]);
      setMemberRoleIds([]);
      setRolesError(String((e && e.message) || e || 'Failed to load roles'));
    } finally {
      setRolesLoading(false);
    }
  }

  function openMenu(e, member) {
    e.preventDefault();
    if (!serverIdForPrefs || !member) return;

    const memberId = member.id != null ? String(member.id) : '';
    const memberName = member.name != null ? String(member.name) : '';
    const p = getParticipantPrefs({ serverId: serverIdForPrefs, participantId: memberId, participantName: memberName });
    const pos = clampMenuPos(e.clientX, e.clientY, 260, 420);

    setPrefs({
      volume: clampInt(p && p.volume != null ? p.volume : 100, 0, 100),
      muted: !!(p && p.muted),
    });

    setMenu({ open: true, x: pos.x, y: pos.y, member });
    setAvailableRoles([]);
    setMemberRoleIds([]);
    setRolesLoading(false);
    setRolesSaving(false);
    setRolesError('');
    setRolesMenuOpen(false);
    setFriendActionBusy(false);
    setFriendActionMsg('');
    setBlockActionBusy(false);
    setBlockActionMsg('');
    setModerationCapabilities(null);
    setModerationLoading(false);
    setModerationActionBusy(false);
    setModerationActionMsg('');

    loadRoleMenuData(member);
  }

  function setVolume(v) {
    if (!menu.member || !serverIdForPrefs) return;
    const memberId = menu.member.id != null ? String(menu.member.id) : '';
    const memberName = menu.member.name != null ? String(menu.member.name) : '';
    const next = setParticipantPrefs({
      serverId: serverIdForPrefs,
      participantId: memberId,
      participantName: memberName,
      volume: clampInt(v, 0, 100),
      muted: prefs.muted,
    });
    setPrefs(next);
  }

  function toggleMuted() {
    if (!menu.member || !serverIdForPrefs) return;
    const memberId = menu.member.id != null ? String(menu.member.id) : '';
    const memberName = menu.member.name != null ? String(menu.member.name) : '';
    const next = setParticipantPrefs({
      serverId: serverIdForPrefs,
      participantId: memberId,
      participantName: memberName,
      volume: prefs.volume,
      muted: !prefs.muted,
    });
    setPrefs(next);
  }

  async function requestFriend() {
    if (!canRequestFriendHere) return;
    if (friendActionBusy) return;

    const targetUserId = menu.member && menu.member.id != null ? toInt(menu.member.id, 0) : 0;
    if (!targetUserId) return;

    setFriendActionBusy(true);
    setFriendActionMsg('');

    try {
      const res = await postComsJson('/friends/request', { user_id: targetUserId });
      const status = String((res && res.status) || '').trim().toLowerCase();

      if (status === 'accepted') {
        setFriendActionMsg('Friend added');
      } else if (status === 'pending') {
        setFriendActionMsg('Request sent');
      } else {
        setFriendActionMsg('Done');
      }
    } catch (e) {
      setFriendActionMsg(String((e && e.message) || e || 'Failed to send request'));
    } finally {
      setFriendActionBusy(false);
    }
  }

  async function toggleBlock() {
    if (!canToggleBlockHere) return;
    if (blockActionBusy) return;

    const targetUserId = menu.member && menu.member.id != null ? toInt(menu.member.id, 0) : 0;
    if (!targetUserId) return;

    setBlockActionBusy(true);
    setBlockActionMsg('');

    try {
      if (menuMemberIsBlocked) {
        await comsApiDelete(`/blocks/${encodeURIComponent(String(targetUserId))}`);
        setBlockActionMsg('User unblocked');
      } else {
        await postComsJson(`/blocks/${encodeURIComponent(String(targetUserId))}`, {});
        setBlockActionMsg('User blocked');
      }

      const blocksRes = await comsApiGet('/blocks').catch(() => null);
      setBlockedUsers(safeArr(blocksRes && blocksRes.blocks));
    } catch (e) {
      setBlockActionMsg(String((e && e.message) || e || 'Failed to update block state'));
    } finally {
      setBlockActionBusy(false);
    }
  }

  async function selectRole(roleId) {
    if (!canManageRolesHere) return;
    if (rolesSaving) return;

    const targetUserId = menu.member && menu.member.id != null ? toInt(menu.member.id, 0) : 0;
    if (!targetUserId || roleId <= 0) return;

    const nextIds = [roleId];

    setRolesSaving(true);
    setRolesError('');
    setMemberRoleIds(nextIds);

    try {
      const res = await putJson(
        `/servers/${encodeURIComponent(serverKey)}/members/${encodeURIComponent(String(targetUserId))}/roles`,
        { role_ids: nextIds }
      );

      const saved = (res && res.ok && Array.isArray(res.roles) ? res.roles : [])
        .map(normalizeRoleRow)
        .filter((r) => r.id > 0 && !isOwnerRole(r));

      const sortedSaved = safeArr(saved).sort((a, b) => {
        if (b.position !== a.position) return b.position - a.position;
        return a.id - b.id;
      });

      const topRoleId = sortedSaved.length ? [sortedSaved[0].id] : [];
      const topColor = pickTopRoleColor(sortedSaved);

      setMemberRoleIds(topRoleId);
      setMemberTopRoleColorByUserId((prev) => ({
        ...prev,
        [String(targetUserId)]: topColor,
      }));
    } catch (e) {
      setRolesError(String((e && e.message) || e || 'Failed to update roles'));
      await loadRoleMenuData(menu.member);
    } finally {
      setRolesSaving(false);
    }
  }

  async function removePinnedMessage(msg) {
    const messageId = toInt(msg && msg.id, 0);
    if (!messageId) return;
    if (!canManagePins) return;
    if (pinActionBusyById[String(messageId)]) return;

    setPinActionError('');
    setPinActionBusyById((prev) => ({ ...prev, [String(messageId)]: true }));

    try {
      let ok = false;

      if (typeof onRemovePinnedMessage === 'function') {
        ok = !!(await onRemovePinnedMessage(msg));
      } else {
        await comsApiPatch(`/messages/${encodeURIComponent(String(messageId))}`, { is_pinned: 0 });
        ok = true;
      }

      if (!ok) {
        setPinActionError('Failed to remove pin');
      }
    } catch (e) {
      setPinActionError(String((e && e.message) || e || 'Failed to remove pin'));
    } finally {
      setPinActionBusyById((prev) => {
        const next = { ...prev };
        delete next[String(messageId)];
        return next;
      });
    }
  }

  // minutes: duration, or 0 to remove the timeout.
  async function handleTimeoutMember(minutes) {
    if (!menuMemberId || !canTimeoutHere || moderationActionBusy) return;
    setModerationActionBusy(true);
    setModerationActionMsg('');
    const url = `/api/coms/servers/${encodeURIComponent(serverKey)}/members/${encodeURIComponent(String(menuMemberId))}/timeout`;
    try {
      const res = await fetch(url, {
        method: minutes ? 'POST' : 'DELETE',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: minutes ? JSON.stringify({ minutes }) : undefined,
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error((data && data.error) || 'Failed to update timeout');
      setModerationActionMsg(minutes ? 'User timed out' : 'Timeout removed');
      setMenu({ open: false, x: 0, y: 0, member: null });
    } catch (e) {
      setModerationActionMsg(String((e && e.message) || e || 'Failed to update timeout'));
    } finally {
      setModerationActionBusy(false);
    }
  }

  // Server mute / deafen (LiveKit-enforced) and moving someone to another voice channel.
  async function handleVoiceModeration(kind, body) {
    if (!menuMemberId || moderationActionBusy) return;
    setModerationActionBusy(true);
    setModerationActionMsg('');
    const base = `/api/coms/servers/${encodeURIComponent(serverKey)}/members/${encodeURIComponent(String(menuMemberId))}`;
    try {
      const res = await fetch(kind === 'move' ? `${base}/move` : `${base}/voice-state`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error((data && data.error) || 'Failed');
      setModerationActionMsg(
        kind === 'move' ? 'Member moved' : body.muted === true ? 'Server-muted' : body.muted === false ? 'Server mute removed' : body.deafened ? 'Deafened' : 'Undeafened'
      );
      setMenu({ open: false, x: 0, y: 0, member: null });
    } catch (e) {
      setModerationActionMsg(String((e && e.message) || e || 'Failed'));
    } finally {
      setModerationActionBusy(false);
    }
  }

  async function handleKickMember() {
    if (!menuMemberId || !canKickHere || moderationActionBusy) return;

    setModerationActionBusy(true);
    setModerationActionMsg('');

    try {
      await postComsJson(
        `/servers/${encodeURIComponent(serverKey)}/members/${encodeURIComponent(String(menuMemberId))}/kick`,
        {}
      );

      setModerationActionMsg('User kicked');
      await Promise.all([refreshBannedMembers(), comsApiGet(`/servers/${encodeURIComponent(serverKey)}/members`).catch(() => null)]);
      setRemoteMembers((prev) => prev.filter((m) => String(normalizeMember(m).id) !== String(menuMemberId)));
      setMenu({ open: false, x: 0, y: 0, member: null });
    } catch (e) {
      setModerationActionMsg(String((e && e.message) || e || 'Failed to kick user'));
    } finally {
      setModerationActionBusy(false);
    }
  }

  async function handleBanMember() {
    if (!menuMemberId || !canBanHere || moderationActionBusy) return;

    setModerationActionBusy(true);
    setModerationActionMsg('');

    try {
      await postComsJson(
        `/servers/${encodeURIComponent(serverKey)}/members/${encodeURIComponent(String(menuMemberId))}/ban`,
        {}
      );

      setModerationActionMsg('User banned');
      setRemoteMembers((prev) => prev.filter((m) => String(normalizeMember(m).id) !== String(menuMemberId)));
      await refreshBannedMembers();
      setMenu({ open: false, x: 0, y: 0, member: null });
    } catch (e) {
      setModerationActionMsg(String((e && e.message) || e || 'Failed to ban user'));
    } finally {
      setModerationActionBusy(false);
    }
  }

  async function handleUnbanMember(member) {
    const targetId = member && member.id != null ? toInt(member.id, 0) : 0;
    if (!targetId || !showBannedTab) return;

    try {
      await postComsJson(
        `/servers/${encodeURIComponent(serverKey)}/members/${encodeURIComponent(String(targetId))}/unban`,
        {}
      );
      await refreshBannedMembers();
    } catch {}
  }

  function renderRadioDot(active, color) {
    return (
      <span
        style={{
          width: 14,
          height: 14,
          borderRadius: 999,
          border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.28 * var(--vx-ov, 1)))',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flex: '0 0 auto',
          background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.03 * var(--vx-ov, 1)))',
        }}
      >
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: 999,
            background: active ? color || '#ffffff' : 'transparent',
            opacity: active ? 1 : 0,
          }}
        />
      </span>
    );
  }

  function renderMemberRow(m) {
    const status = String(m.status || 'offline').toLowerCase();
    const isOnline = isShownOnline(status);
    const isMe = toInt(m.id, 0) > 0 && toInt(m.id, 0) === toInt(myUserId, 0);
    const rowOpacity = isOnline && status !== 'invisible' ? 1 : 0.55;
    const nameColor = String(m.roleColor || '').trim();
    const customText = String(m.custom_text || '').trim();
    const label = status === 'idle' ? 'AFK' : status === 'dnd' ? 'do not disturb' : status === 'invisible' ? 'invisible (only you see this)' : isOnline ? 'online' : 'offline';

    return (
      <div
        key={m.id}
        className={`coms-member ${isOnline ? 'online' : 'offline'}`}
        onContextMenu={(e) => openMenu(e, m)}
        title={`${statusTitle(status, customText)}${isMe ? '' : ' · Right-click for member actions'}`}
        data-presence={status}
        style={{
          opacity: rowOpacity,
          filter: isOnline ? 'none' : 'saturate(0.75)',
        }}
      >
        <StatusDot status={status} size={10} />
        <div className="coms-member-name" style={{ color: nameColor || 'inherit', minWidth: 0 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{m.name}</span>
            <StatusTag status={status} />
          </span>
          {customText ? (
            <span className="vx-pres-custom" style={{ color: 'var(--vx-fg, inherit)' }} data-testid="presence-custom-text">
              {customText}
            </span>
          ) : null}
        </div>
        <div className="coms-member-status" style={{ opacity: isOnline ? 1 : 0.85 }}>
          {isMe ? (
            <button type="button" className="vx-link-btn" style={{ font: 'inherit', color: 'inherit', padding: 0 }} onClick={openMySettings} title="Change your status">
              {label}
            </button>
          ) : (
            label
          )}
        </div>
      </div>
    );
  }

  function openMySettings() {
    try {
      window.__voxPendingSettingsTab = 'privacy';
      window.dispatchEvent(new CustomEvent('vox-open-settings', { detail: { tab: 'privacy' } }));
    } catch {}
  }

  function renderBannedMemberRow(m) {
    const nameColor = String(m.roleColor || '').trim();

    return (
      <div
        key={m.id}
        className="coms-member offline"
        onContextMenu={(e) => openMenu(e, m)}
        title="Right-click for member actions"
        style={{
          opacity: 0.82,
          filter: 'saturate(0.75)',
        }}
      >
        <div
          className="coms-dot offline"
          style={{
            width: 10,
            height: 10,
            borderRadius: 999,
            flex: '0 0 auto',
            background: '#7f1d1d',
            opacity: 0.95,
            boxShadow: '0 0 8px rgba(220,38,38,0.35)',
            border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.18 * var(--vx-ov, 1)))',
          }}
        />
        <div
          className="coms-member-name"
          style={{
            color: nameColor || 'inherit',
          }}
        >
          {m.name}
        </div>
        <div className="coms-member-status" style={{ opacity: 0.95 }}>
          banned
        </div>
      </div>
    );
  }

  function renderPinsTab() {
    if (!normalizedPinnedMessages.length) {
      return (
        <div
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            overflowX: 'hidden',
            padding: '14px 12px',
            fontSize: 12,
            opacity: 0.7,
            lineHeight: 1.4,
          }}
        >
          No pinned messages yet
        </div>
      );
    }

    return (
      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
        <div style={{ display: 'grid', gap: 8, padding: 8, minWidth: 0 }}>
          {pinActionError ? (
            <div
              style={{
                padding: '8px 10px',
                borderRadius: 8,
                fontSize: 12,
                lineHeight: 1.35,
                color: '#fecaca',
                background: 'rgba(127,29,29,0.30)',
                border: '1px solid rgba(248,113,113,0.25)',
              }}
            >
              {pinActionError}
            </div>
          ) : null}

          {normalizedPinnedMessages.map((msg) => {
            const preview = buildPinnedPreview(msg);
            const author = String(msg.author || '').trim() || `User ${toInt(msg.user_id, 0)}`;
            const stamp = String(msg.stamp || '').trim();
            const canJump = typeof onJumpToPinnedMessage === 'function';
            const busy = !!pinActionBusyById[String(msg.id)];

            return (
              <div
                key={msg.id}
                style={{
                  width: '100%',
                  minWidth: 0,
                  textAlign: 'left',
                  border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.10 * var(--vx-ov, 1)))',
                  background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.035 * var(--vx-ov, 1)))',
                  color: 'inherit',
                  borderRadius: 10,
                  padding: '10px 10px 9px',
                  boxSizing: 'border-box',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    minWidth: 0,
                    marginBottom: 4,
                  }}
                >
                  <span
                    style={{
                      flexShrink: 0,
                      fontSize: 12,
                      lineHeight: 1,
                      opacity: 0.9,
                    }}
                  >
                    📌
                  </span>
                  <span
                    style={{
                      fontSize: 12,
                      fontWeight: 700,
                      minWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {author}
                  </span>
                  {stamp ? (
                    <span
                      style={{
                        fontSize: 11,
                        opacity: 0.65,
                        marginLeft: 'auto',
                        flexShrink: 0,
                      }}
                    >
                      {stamp}
                    </span>
                  ) : null}
                </div>

                <button
                  type="button"
                  onClick={() => {
                    if (typeof onJumpToPinnedMessage === 'function') onJumpToPinnedMessage(msg);
                  }}
                  style={{
                    width: '100%',
                    minWidth: 0,
                    textAlign: 'left',
                    border: 'none',
                    background: 'transparent',
                    color: 'inherit',
                    padding: 0,
                    cursor: canJump ? 'pointer' : 'default',
                  }}
                  title={canJump ? 'Jump to message' : preview}
                >
                  <div
                    style={{
                      fontSize: 12,
                      lineHeight: 1.4,
                      opacity: 0.88,
                      wordBreak: 'break-word',
                      overflowWrap: 'anywhere',
                    }}
                  >
                    {preview}
                  </div>
                </button>

                {canManagePins ? (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      marginTop: 8,
                    }}
                  >
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        removePinnedMessage(msg);
                      }}
                      disabled={busy}
                      style={{
                        height: 26,
                        padding: '0 9px',
                        borderRadius: 8,
                        border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.12 * var(--vx-ov, 1)))',
                        background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.06 * var(--vx-ov, 1)))',
                        color: 'inherit',
                        cursor: busy ? 'default' : 'pointer',
                        fontSize: 11,
                        opacity: busy ? 0.7 : 0.92,
                      }}
                      title="Remove pin"
                    >
                      {busy ? 'Removing…' : 'Remove pin'}
                    </button>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  function renderMembersTab() {
    return (
      <div className="coms-members-list" style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}>
        {onlineMembers.map(renderMemberRow)}

        {onlineMembers.length && offlineMembers.length ? (
          <div
            style={{
              margin: '8px 10px',
              marginLeft: 26,
              height: 1,
              background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.10 * var(--vx-ov, 1)))',
              borderRadius: 999,
            }}
          />
        ) : null}

        {offlineMembers.map(renderMemberRow)}
      </div>
    );
  }

  function renderBannedTab() {
    if (!showBannedTab) {
      return null;
    }

    if (bannedMembersLoading && !bannedMembersLoaded) {
      return (
        <div
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            overflowX: 'hidden',
            padding: '14px 12px',
            fontSize: 12,
            opacity: 0.75,
            lineHeight: 1.4,
          }}
        >
          Loading banned users…
        </div>
      );
    }

    if (!enrichedBannedMembers.length) {
      return (
        <div
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            overflowX: 'hidden',
            padding: '14px 12px',
            fontSize: 12,
            opacity: 0.7,
            lineHeight: 1.4,
          }}
        >
          No banned users
        </div>
      );
    }

    return (
      <div className="coms-members-list" style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}>
        {enrichedBannedMembers.map(renderBannedMemberRow)}
      </div>
    );
  }

  return (
    <div
      className="coms-members"
      style={{
        position: 'relative',
        zIndex: 10,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
      }}
    >
      <div
        style={{
          padding: '10px 10px 12px',
          borderBottom: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.06 * var(--vx-ov, 1)))',
          background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.015 * var(--vx-ov, 1)))',
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <input
            type="text"
            value={searchValue}
            onChange={(e) => {
              if (typeof onMessageSearchChange === 'function') onMessageSearchChange(e.target.value);
            }}
            placeholder="Search messages…"
            style={{
              flex: 1,
              minWidth: 0,
              height: 28,
              borderRadius: 8,
              border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.14 * var(--vx-ov, 1)))',
              background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.04 * var(--vx-ov, 1)))',
              color: 'inherit',
              outline: 'none',
              padding: '0 9px',
              fontSize: 12,
            }}
            title="Filters visible chat messages"
          />

          {searchNorm ? (
            <button
              type="button"
              onClick={() => {
                if (typeof onMessageSearchChange === 'function') onMessageSearchChange('');
              }}
              style={{
                height: 28,
                padding: '0 8px',
                borderRadius: 8,
                border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.14 * var(--vx-ov, 1)))',
                background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.04 * var(--vx-ov, 1)))',
                color: 'inherit',
                cursor: 'pointer',
                fontSize: 11,
                flexShrink: 0,
              }}
            >
              Clear
            </button>
          ) : null}
        </div>

        <div
          style={{
            height: 1,
            width: 'calc(100% + 20px)',
            background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.06 * var(--vx-ov, 1)))',
            borderRadius: 999,
            marginTop: 5,
            marginLeft: -10,
          }}
        />

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <button
            type="button"
            onClick={() => {
              if (typeof onPanelTabChange === 'function') onPanelTabChange('members');
            }}
            style={{
              height: 28,
              padding: '0 10px',
              borderRadius: 8,
              border: activeTab === 'members' ? '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.18 * var(--vx-ov, 1)))' : '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.10 * var(--vx-ov, 1)))',
              background: activeTab === 'members' ? 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.10 * var(--vx-ov, 1)))' : 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.04 * var(--vx-ov, 1)))',
              color: 'inherit',
              cursor: 'pointer',
              fontSize: 12,
              fontWeight: activeTab === 'members' ? 700 : 500,
            }}
            title="Members"
          >
            👪
          </button>

          <button
            type="button"
            onClick={() => {
              if (typeof onPanelTabChange === 'function') onPanelTabChange('pins');
            }}
            style={{
              height: 28,
              padding: '0 10px',
              borderRadius: 8,
              border: activeTab === 'pins' ? '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.18 * var(--vx-ov, 1)))' : '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.10 * var(--vx-ov, 1)))',
              background: activeTab === 'pins' ? 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.10 * var(--vx-ov, 1)))' : 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.04 * var(--vx-ov, 1)))',
              color: 'inherit',
              cursor: 'pointer',
              fontSize: 12,
              fontWeight: activeTab === 'pins' ? 700 : 500,
            }}
            title="Pinned messages"
          >
            📌
            {normalizedPinnedMessages.length ? (
              <span style={{ marginLeft: 6, opacity: 0.7 }}>{normalizedPinnedMessages.length}</span>
            ) : null}
          </button>

          {showBannedTab ? (
            <button
              type="button"
              onClick={() => {
                if (typeof onPanelTabChange === 'function') onPanelTabChange('banned');
              }}
              style={{
                height: 28,
                padding: '0 10px',
                borderRadius: 8,
                border: activeTab === 'banned' ? '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.18 * var(--vx-ov, 1)))' : '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.10 * var(--vx-ov, 1)))',
                background: activeTab === 'banned' ? 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.10 * var(--vx-ov, 1)))' : 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.04 * var(--vx-ov, 1)))',
                color: 'inherit',
                cursor: 'pointer',
                fontSize: 12,
                fontWeight: activeTab === 'banned' ? 700 : 500,
              }}
              title="Banned users"
            >
              ⛔
              {enrichedBannedMembers.length ? (
                <span style={{ marginLeft: 6, opacity: 0.7 }}>{enrichedBannedMembers.length}</span>
              ) : null}
            </button>
          ) : null}
        </div>
      </div>

      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {activeTab === 'pins' ? renderPinsTab() : activeTab === 'banned' ? renderBannedTab() : renderMembersTab()}
      </div>

      <div
        style={{
          flexShrink: 0,
          height: SUPPORT_BANNER_HEIGHT,
          padding: '10px',
          boxSizing: 'border-box',
          borderTop: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.08 * var(--vx-ov, 1)))',
          background: 'linear-gradient(180deg, rgba(var(--vx-fg-rgb, 255,255,255), calc(0.02 * var(--vx-ov, 1))), rgba(var(--vx-fg-rgb, 255,255,255), calc(0.05 * var(--vx-ov, 1))))',
        }}
      >
        <button
          type="button"
          onClick={handleBmcPopup}
          style={{
            width: '100%',
            height: '100%',
            border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.14 * var(--vx-ov, 1)))',
            borderRadius: 10,
            background: 'linear-gradient(135deg, rgba(var(--vx-fg-rgb, 255,255,255), calc(0.08 * var(--vx-ov, 1))), rgba(var(--vx-fg-rgb, 255,255,255), calc(0.04 * var(--vx-ov, 1))))',
            color: 'inherit',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 10,
            fontSize: 13,
            fontWeight: 700,
            letterSpacing: '0.01em',
            boxShadow: '0 6px 18px rgba(0,0,0,0.18)',
          }}
          title="Support Voxemera"
        >
          <span role="img" aria-label="coffee" style={{ fontSize: 16 }}>☕</span>
          <span
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              lineHeight: 1.05,
            }}
          >
            <span>Support Voxemera</span>
            <span
              style={{
                fontSize: 11,
                fontWeight: 600,
                opacity: 0.72,
              }}
            >
              by Point Atlas
            </span>
          </span>
        </button>
      </div>

      {menu.open ? (
        <div
          ref={menuRef}
          className="coms-member-menu"
          style={{
            position: 'fixed',
            left: menu.x,
            top: menu.y,
            width: 260,
            zIndex: 5000,
          }}
        >
          <div className="coms-member-menu-title">
            {menu.member && menu.member.name ? menu.member.name : 'Member'}
          </div>

          {!menuMemberIsBanned ? (
            <>
              <div className="coms-member-menu-row">
                <div className="coms-member-menu-label">Volume</div>
                <div className="coms-member-menu-value">{prefs.volume}%</div>
              </div>

              <input
                className="coms-member-menu-slider"
                type="range"
                min="0"
                max="100"
                value={prefs.volume}
                onChange={(e) => setVolume(e.target.value)}
              />

              <button className="coms-member-menu-btn" onClick={toggleMuted}>
                {prefs.muted ? 'Unmute user' : 'Mute user'}
              </button>
            </>
          ) : null}

          {canRequestFriendHere ? (
            <button className="coms-member-menu-btn" onClick={requestFriend} disabled={friendActionBusy}>
              {friendActionBusy ? 'Sending…' : 'Add friend'}
            </button>
          ) : null}

          {canToggleBlockHere ? (
            <button className="coms-member-menu-btn" onClick={toggleBlock} disabled={blockActionBusy}>
              {blockActionBusy
                ? menuMemberIsBlocked
                  ? 'Unblocking…'
                  : 'Blocking…'
                : menuMemberIsBlocked
                  ? 'Unblock user'
                  : 'Block user'}
            </button>
          ) : null}

          {canManageRolesHere ? (
            <button
              className="coms-member-menu-btn"
              onMouseEnter={() => setRolesMenuOpen(true)}
              onClick={() => setRolesMenuOpen((v) => !v)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <span>Role</span>
              <span style={{ opacity: 0.7 }}>▸</span>
            </button>
          ) : null}

          {menuMemberId && menuMemberId !== myUserId && serverKey ? (
            <button
              className="coms-member-menu-btn"
              onClick={() => window.dispatchEvent(new CustomEvent('vox-report', { detail: { type: 'user', serverKey, userId: menuMemberId, label: String((menu.member && (menu.member.display_name || menu.member.name)) || '') } }))}
            >
              Report member
            </button>
          ) : null}

          {canModerateMembers && menuMemberId && menuMemberId !== myUserId ? (
            <button className="coms-member-menu-btn" onClick={() => window.dispatchEvent(new CustomEvent('vox-open-server-admin', { detail: { section: 'members' } }))}>
              Warn, history & more…
            </button>
          ) : null}

          {canModerateMembers && menuMemberId && menuMemberId !== myUserId ? (
            <>
              {moderationLoading ? (
                <div
                  style={{
                    marginTop: 8,
                    fontSize: 12,
                    opacity: 0.75,
                    lineHeight: 1.35,
                  }}
                >
                  Checking moderation…
                </div>
              ) : null}

              {!moderationLoading && canTimeoutHere ? (
                menuTimeoutUntil > Date.now() ? (
                  <button className="coms-member-menu-btn" onClick={() => handleTimeoutMember(0)} disabled={moderationActionBusy} title={`Timed out until ${new Date(menuTimeoutUntil).toLocaleString()}`}>
                    Remove timeout
                  </button>
                ) : (
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', padding: '2px 0' }}>
                    <span style={{ fontSize: 12, opacity: 0.75, alignSelf: 'center' }}>Time out:</span>
                    {[
                      [5, '5m'],
                      [60, '1h'],
                      [1440, '1d'],
                      [10080, '1w'],
                    ].map(([m, label]) => (
                      <button key={m} className="coms-member-menu-btn" style={{ width: 'auto', padding: '2px 8px' }} onClick={() => handleTimeoutMember(m)} disabled={moderationActionBusy}>
                        {label}
                      </button>
                    ))}
                  </div>
                )
              ) : null}

              {!moderationLoading && (canVoiceMute || canVoiceDeafen) && !menuMemberIsBanned ? (
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', padding: '2px 0' }}>
                  <span style={{ fontSize: 12, opacity: 0.75, alignSelf: 'center' }}>Voice:</span>
                  {canVoiceMute ? (
                    <>
                      <button className="coms-member-menu-btn" style={{ width: 'auto', padding: '2px 8px' }} onClick={() => handleVoiceModeration('state', { muted: true })} disabled={moderationActionBusy}>
                        Server mute
                      </button>
                      <button className="coms-member-menu-btn" style={{ width: 'auto', padding: '2px 8px' }} onClick={() => handleVoiceModeration('state', { muted: false })} disabled={moderationActionBusy}>
                        Unmute
                      </button>
                    </>
                  ) : null}
                  {canVoiceDeafen ? (
                    <>
                      <button className="coms-member-menu-btn" style={{ width: 'auto', padding: '2px 8px' }} onClick={() => handleVoiceModeration('state', { deafened: true })} disabled={moderationActionBusy}>
                        Deafen
                      </button>
                      <button className="coms-member-menu-btn" style={{ width: 'auto', padding: '2px 8px' }} onClick={() => handleVoiceModeration('state', { deafened: false })} disabled={moderationActionBusy}>
                        Undeafen
                      </button>
                    </>
                  ) : null}
                </div>
              ) : null}

              {!moderationLoading && canVoiceMove && voiceChannelsForMove.length > 1 && !menuMemberIsBanned ? (
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', padding: '2px 0' }}>
                  <span style={{ fontSize: 12, opacity: 0.75, alignSelf: 'center' }}>Move to:</span>
                  {voiceChannelsForMove.map((c) => (
                    <button key={c.id} className="coms-member-menu-btn" style={{ width: 'auto', padding: '2px 8px' }} onClick={() => handleVoiceModeration('move', { to_channel_id: c.id })} disabled={moderationActionBusy}>
                      {c.name}
                    </button>
                  ))}
                </div>
              ) : null}

              {!moderationLoading && canKickHere ? (
                <button
                  className="coms-member-menu-btn"
                  onClick={handleKickMember}
                  disabled={moderationActionBusy}
                >
                  {moderationActionBusy ? 'Working…' : 'Kick user'}
                </button>
              ) : null}

              {!moderationLoading && canBanHere ? (
                <button
                  className="coms-member-menu-btn"
                  onClick={handleBanMember}
                  disabled={moderationActionBusy}
                >
                  {moderationActionBusy ? 'Working…' : 'Ban user'}
                </button>
              ) : null}

              {!moderationLoading && canUnbanHere ? (
                <button
                  className="coms-member-menu-btn"
                  onClick={async () => {
                    if (!menu.member || moderationActionBusy) return;
                    setModerationActionBusy(true);
                    setModerationActionMsg('');
                    try {
                      await handleUnbanMember(menu.member);
                      setModerationActionMsg('User unbanned');
                      setMenu({ open: false, x: 0, y: 0, member: null });
                    } catch (e) {
                      setModerationActionMsg(String((e && e.message) || e || 'Failed to unban user'));
                    } finally {
                      setModerationActionBusy(false);
                    }
                  }}
                  disabled={moderationActionBusy}
                >
                  {moderationActionBusy ? 'Working…' : 'Unban user'}
                </button>
              ) : null}
            </>
          ) : null}

          {friendActionMsg ? (
            <div
              style={{
                marginTop: 8,
                fontSize: 12,
                opacity: 0.8,
                lineHeight: 1.35,
              }}
            >
              {friendActionMsg}
            </div>
          ) : null}

          {blockActionMsg ? (
            <div
              style={{
                marginTop: 8,
                fontSize: 12,
                opacity: 0.8,
                lineHeight: 1.35,
              }}
            >
              {blockActionMsg}
            </div>
          ) : null}

          {moderationActionMsg ? (
            <div
              style={{
                marginTop: 8,
                fontSize: 12,
                opacity: 0.82,
                lineHeight: 1.35,
              }}
            >
              {moderationActionMsg}
            </div>
          ) : null}

          {!moderationLoading && moderationCapabilities && !canBanHere && !canKickHere && !canUnbanHere && (moderationCapabilities.ban_error || moderationCapabilities.kick_error) ? (
            <div
              style={{
                marginTop: 8,
                fontSize: 12,
                opacity: 0.72,
                lineHeight: 1.35,
              }}
            >
              {String(moderationCapabilities.ban_error || moderationCapabilities.kick_error || '').trim()}
            </div>
          ) : null}
        </div>
      ) : null}

      {menu.open && canManageRolesHere && rolesMenuOpen ? (
        <div
          ref={rolesMenuRef}
          className="coms-member-menu"
          style={{
            position: 'fixed',
            left: rolesMenuPos.x,
            top: rolesMenuPos.y,
            width: 240,
            zIndex: 5001,
            maxHeight: 320,
            overflowY: 'auto',
          }}
          onMouseLeave={() => setRolesMenuOpen(false)}
        >
          <div className="coms-member-menu-title">Select role</div>

          {rolesLoading ? (
            <div style={{ fontSize: 12, opacity: 0.75 }}>Loading roles…</div>
          ) : null}

          {!rolesLoading && rolesError ? (
            <div style={{ fontSize: 12, opacity: 0.8, color: '#fca5a5' }}>{rolesError}</div>
          ) : null}

          {!rolesLoading && !rolesError && !sortedAvailableRoles.length ? (
            <div style={{ fontSize: 12, opacity: 0.75 }}>No assignable roles</div>
          ) : null}

          {!rolesLoading && !rolesError
            ? sortedAvailableRoles.map((role) => {
                const roleId = toInt(role && role.id, 0);
                const active = selectedRoleId === roleId;
                const color = String(role && role.color ? role.color : '').trim();

                return (
                  <button
                    key={roleId}
                    className="coms-member-menu-btn"
                    onClick={() => selectRole(roleId)}
                    disabled={rolesSaving}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      width: '100%',
                      justifyContent: 'flex-start',
                      opacity: rolesSaving ? 0.75 : 1,
                    }}
                  >
                    {renderRadioDot(active, color)}
                    <span
                      style={{
                        color: color || 'inherit',
                        fontWeight: active ? 700 : 500,
                      }}
                    >
                      {role.name}
                    </span>
                  </button>
                );
              })
            : null}
        </div>
      ) : null}
    </div>
  );
}